import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  business_name: "",
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name, please.";
    if (!form.email.trim()) e.email = "An email so we can reply.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Hmm, that email doesn’t look right.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Tell us a bit more (at least a sentence).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;
    setStatus("submitting");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        business_name: form.business_name.trim(),
        project_type: "website",
        message: form.message.trim(),
        website: form.website,
        source: "inline-contact",
      });
      setStatus("success");
      toast.success("Got it — we'll reply within 24 hours.");
    } catch (err) {
      console.error("lead submit failed", err);
      setStatus("error");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-[rgba(10,10,10,0.08)]"
    >
      <div className="section-container">
        <div className="mx-auto max-w-[860px]">
          <div className="reveal tick-label" data-testid="contact-eyebrow">
            Contact
          </div>
          <h2
            className="reveal font-sans-display mt-4 sm:mt-5 text-[34px] sm:text-[56px] lg:text-[72px] font-extrabold leading-[1.05] sm:leading-[1.02] tracking-[-0.04em] text-[#0a0a0a]"
            data-testid="contact-heading"
          >
            Let’s build{" "}
            <span className="hl-marker">something</span>
            <br />
            you’re{" "}
            <span className="font-display italic font-normal">proud</span> of.
          </h2>
          <p
            className="reveal reveal-delay-1 mt-4 sm:mt-5 text-[15.5px] sm:text-[17px] leading-[1.6] text-[#0a0a0a]/70 max-w-[560px]"
            data-testid="contact-subheading"
          >
            Tell us a little about your business and what you’re thinking. We
            read every message ourselves and usually reply within a day.
          </p>

          <div className="reveal reveal-delay-2 mt-5 sm:mt-6 inline-flex items-center gap-2 text-[12.5px] sm:text-[13px] text-[#0a0a0a]/70">
            <span className="status-dot" aria-hidden />
            <span className="font-medium text-[#0a0a0a]">
              Currently available
            </span>
            <span className="text-[#0a0a0a]/40">•</span>
            <span>Replies within 24h</span>
          </div>

          <div
            className="reveal reveal-delay-3 mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
            data-testid="contact-direct"
          >
            <a
              href="mailto:hello@crestonagency.com"
              className="group inline-flex items-center gap-3 rounded-full border border-[rgba(10,10,10,0.12)] bg-white px-4 py-2.5 text-[13.5px] sm:text-[14px] text-[#0a0a0a] hover:border-[#0a0a0a] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-16px_rgba(10,10,10,0.25)] transition-all duration-200"
              data-testid="contact-email-link"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0a0a0a] text-[#fbf9f4]">
                <Mail className="size-3.5" />
              </span>
              <span className="font-medium">hello@crestonagency.com</span>
            </a>
            <a
              href="tel:+18452628024"
              className="group inline-flex items-center gap-3 rounded-full border border-[rgba(10,10,10,0.12)] bg-white px-4 py-2.5 text-[13.5px] sm:text-[14px] text-[#0a0a0a] hover:border-[#0a0a0a] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-16px_rgba(10,10,10,0.25)] transition-all duration-200"
              data-testid="contact-phone-link-mark"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0a0a0a] text-[#fbf9f4]">
                <Phone className="size-3.5" />
              </span>
              <span className="flex items-baseline gap-2">
                <span className="font-mono-mark text-[10.5px] tracking-[0.12em] uppercase text-[#0a0a0a]/50">
                  New projects
                </span>
                <span className="font-medium">+1 (845) 262-8024</span>
              </span>
            </a>
            <a
              href="tel:+18482366560"
              className="group inline-flex items-center gap-3 rounded-full border border-[rgba(10,10,10,0.12)] bg-white px-4 py-2.5 text-[13.5px] sm:text-[14px] text-[#0a0a0a] hover:border-[#0a0a0a] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-16px_rgba(10,10,10,0.25)] transition-all duration-200"
              data-testid="contact-phone-link-joe"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0a0a0a] text-[#fbf9f4]">
                <Phone className="size-3.5" />
              </span>
              <span className="flex items-baseline gap-2">
                <span className="font-mono-mark text-[10.5px] tracking-[0.12em] uppercase text-[#0a0a0a]/50">
                  Support
                </span>
                <span className="font-medium">+1 (848) 236-6560</span>
              </span>
            </a>
          </div>

          <div
            className="reveal reveal-delay-4 relative mt-8 sm:mt-10 rounded-[22px] border border-[rgba(10,10,10,0.1)] bg-white shadow-[0_30px_60px_-30px_rgba(10,10,10,0.22)] overflow-hidden"
            data-testid="contact-card"
          >
            {/* subtle corner grid accent */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-12 -right-12 w-64 h-64 dot-grid-fine opacity-50"
              style={{
                WebkitMaskImage:
                  "radial-gradient(circle at 70% 30%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 70%)",
                maskImage:
                  "radial-gradient(circle at 70% 30%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 70%)",
              }}
            />
            {status === "success" ? (
              <SuccessPanel name={form.name} />
            ) : (
              <form onSubmit={onSubmit} className="p-5 sm:p-10" noValidate>
                {/* honeypot */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    height: 1,
                    width: 1,
                    overflow: "hidden",
                  }}
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    id="name"
                    label="Your name"
                    placeholder="Alex Morgan"
                    value={form.name}
                    onChange={set("name")}
                    error={errors.name}
                    testid="contact-name-input"
                    errorTestid="contact-name-error"
                  />
                  <Field
                    id="email"
                    type="email"
                    label="Email"
                    placeholder="you@business.com"
                    value={form.email}
                    onChange={set("email")}
                    error={errors.email}
                    testid="contact-email-input"
                    errorTestid="contact-email-error"
                  />
                </div>

                <div className="mt-5">
                  <Field
                    id="business"
                    label="Business name"
                    optional
                    placeholder="Northwind Coffee"
                    value={form.business_name}
                    onChange={set("business_name")}
                    testid="contact-business-input"
                  />
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="message"
                    className="block text-[13px] font-medium text-[#0a0a0a]"
                  >
                    What are you thinking about?
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="A new site, a redesign, a quick landing page… tell us where you’re at and where you want to go."
                    className="input-polished mt-2 w-full rounded-2xl border border-[rgba(10,10,10,0.14)] bg-white px-4 py-3 text-[15px] text-[#0a0a0a] placeholder:text-[#0a0a0a]/35 focus:outline-none focus:border-[#0a0a0a] focus:ring-0 resize-none transition-shadow duration-150"
                    data-testid="contact-message-textarea"
                    aria-invalid={Boolean(errors.message)}
                  />
                  {errors.message ? (
                    <p
                      className="mt-2 text-[12.5px] text-[#c0392b]"
                      data-testid="contact-message-error"
                    >
                      {errors.message}
                    </p>
                  ) : null}
                </div>

                {status === "error" ? (
                  <p
                    className="mt-5 text-[13px] text-[#c0392b]"
                    data-testid="contact-submit-error"
                  >
                    Something went wrong. Please try again in a moment.
                  </p>
                ) : null}

                <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-[12.5px] text-[#0a0a0a]/50">
                    We reply within 24 hours — no newsletters, no spam.
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="group inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#0a0a0a] text-[#fbf9f4] text-[14.5px] font-semibold btn-hard disabled:opacity-70 disabled:cursor-not-allowed w-full sm:w-auto"
                    data-testid="contact-submit-button"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send message
                        <ArrowRight className="ml-2 size-4 btn-arrow" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  error,
  optional,
  testid,
  errorTestid,
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="block text-[13px] font-medium text-[#0a0a0a]">
          {label}
        </label>
        {optional ? (
          <span className="text-[11.5px] text-[#0a0a0a]/40">optional</span>
        ) : null}
      </div>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input-polished mt-2 w-full h-12 rounded-2xl border border-[rgba(10,10,10,0.14)] bg-white px-4 text-[15px] text-[#0a0a0a] placeholder:text-[#0a0a0a]/35 focus:outline-none focus:border-[#0a0a0a] transition-shadow duration-150"
        data-testid={testid}
        aria-invalid={Boolean(error)}
      />
      {error ? (
        <p
          className="mt-2 text-[12.5px] text-[#c0392b]"
          data-testid={errorTestid}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SuccessPanel({ name }) {
  const first = (name || "friend").split(" ")[0] || "friend";
  return (
    <div
      className="p-10 sm:p-14 text-center"
      data-testid="contact-success-message"
    >
      <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#0a0a0a] text-[#fbf9f4]">
        <Check className="size-6" />
      </div>
      <h3 className="font-sans-display mt-6 text-[28px] sm:text-[34px] font-bold tracking-[-0.02em] text-[#0a0a0a]">
        Thanks, {first}.
      </h3>
      <p className="mt-3 max-w-md mx-auto text-[15px] text-[#0a0a0a]/65 leading-relaxed">
        Your message just landed in our inbox. We’ll read it carefully and
        reply within 24 hours.
      </p>
    </div>
  );
}
